import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { Box, Button, Chip, LinearProgress, Typography } from '@mui/material'
import { H3 } from '../../../layouts/TextStyles'
import { userApiSlice } from '../../../features/user/userSlice'
import { curatorStudentsApiSlice } from '../../../features/curator/curatorStudents'
import CustomInput from '../../../components/muiComponents/customInput'
import StudentsTable from './components/StudentsTable'
import { capitalize } from '../../../utils/functions'

const styles = {
    boxContainer: {
        margin: '10px',
        borderRadius: '16px',
    },
    filterBox: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 2,
    },
    searchBox: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
    },
    search: {
        width: '440px',
    },
}

const StudentListPage = () => {
    const [searchParams, setSearchParams] = useSearchParams('')
    const { formatMessage, locale } = useIntl()
    const [filter, setFilter] = useState('')
    const [selectedCourseId, setSelectedCourseId] = useState<number | undefined>(undefined)
    const [page, setPage] = React.useState(
        Number(searchParams.get('page')) || 1,
    )
    const [rowsPerPage, setRowsPerPage] = React.useState(
        Number(searchParams.get('size')) || 5,
    )
    const [fetchStudents, { data: studentResponse = null, isFetching }] =
        curatorStudentsApiSlice.useLazyFetchAllStudentsQuery()
    const [fetchCourses, { data: courseResponse = null, isFetching: isFetchingCourses }] =
        curatorStudentsApiSlice.useLazyFetchCuratorCoursesQuery()
    const [resetTemporaryPassword, { isLoading: isReseting }] =
        userApiSlice.useResetTemporaryPasswordMutation()

    const handleResetPassword = async (id: number) => {
        try {
            await resetTemporaryPassword(id).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            getStudents(selectedCourseId)
        } catch (error) { }
    }

    const getStudents = (courseId?: number) => {
        fetchStudents({
            filter,
            page: page - 1,
            size: rowsPerPage,
            courseId: courseId,
        })

        setQueryParams()
    }

    const setQueryParams = () => {
        const params = {
            page: page.toString(),
            size: rowsPerPage.toString(),
        }
        setSearchParams(params)
    }

    const handleCourseClick = (courseId: number) => {
        setSelectedCourseId(courseId)
        getStudents(courseId)
    }

    useEffect(() => {
        fetchCourses()
    }, [])

    useEffect(() => {
        setQueryParams()
        if (selectedCourseId) {
            getStudents(selectedCourseId)
        }
    }, [rowsPerPage, page])

    useEffect(() => {
        if (courseResponse?.data?.length > 0 && !selectedCourseId) {
            const firstCourseId = courseResponse.data[0].id
            setSelectedCourseId(firstCourseId)
            getStudents(firstCourseId)
        }
    }, [courseResponse])

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Title>
                        <H3>{formatMessage({ id: 'students' })}</H3>
                    </Title>
                </Header>

                <Box sx={styles.filterBox}>
                    <Box sx={styles.searchBox}>
                        <CustomInput
                            sx={styles.search}
                            value={filter}
                            margin="none"
                            type="text"
                            placeholder={formatMessage({
                                id: 'searchByStudentName',
                            })}
                            size="small"
                            onChange={(e) => setFilter(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    getStudents(selectedCourseId)
                                }
                            }}
                        />

                        <Button
                            variant="contained"
                            onClick={() => getStudents(selectedCourseId)}
                        >
                            {formatMessage({ id: 'search' })}
                        </Button>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <ChipGroup>
                        {courseResponse?.data?.map((course) => (
                            <Chip
                                label={course[`title${capitalize(locale)}`]}
                                key={course.id}
                                onClick={() => handleCourseClick(course.id)}
                                sx={{
                                    cursor: 'pointer',
                                    background: selectedCourseId === course.id ? '#E62D95' : '#E0E2E7',
                                    color: selectedCourseId === course.id ? '#fff' : '#000'
                                }}
                            />
                        ))}
                    </ChipGroup>
                    <Typography variant="body1" sx={{ color: '#858D9D' }}>
                        {formatMessage({ id: 'all' })} {' : '}
                        <span style={{ fontWeight: 600, color: '#E62D95' }}>{studentResponse?.totalCount} {formatMessage({ id: 'student' })}</span>
                    </Typography>
                </Box>
                {isFetching || isReseting ? (
                    <LinearProgress />
                ) : (
                    studentResponse && (
                        <StudentsTable
                            studentsData={studentResponse.data}
                            pageNumber={studentResponse.pageNumber}
                            totalCount={studentResponse.totalCount}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            isFetching={isFetching}
                            setPage={setPage}
                            setRowsPerPage={setRowsPerPage}
                            onResetPassword={handleResetPassword}
                        />
                    )
                )}
            </Content>
        </Wrapper>
    )
}

export default StudentListPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`
const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: #fff;
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`

const ChipGroup = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin: 20px 0;
`